import { useState, useCallback, useEffect, useRef, createContext, useContext, type ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import { setUser as setSentryUser } from "@/lib/sentry";
import { getSessionTimeoutMs } from "@shared/sessionPolicy";

// ─── Types ──────────────────────────────────────────────────────────
export type UserRole = "admin" | "staff" | "driver";

export interface AuthUser {
    id: number;
    username: string;
    displayName: string;
    role: UserRole;
}

export type LoginFailureReason =
    | "invalid_credentials"
    | "rate_limited"
    | "service_unavailable"
    | "session_unavailable";

export type LoginResult = { ok: true } | { ok: false; reason: LoginFailureReason };

interface AuthState {
    user: AuthUser | null;
    isAuthenticated: boolean;
    role: UserRole | null;
    /** backward compat – equals `role === 'admin'` */
    isAdmin: boolean;
    isReady: boolean;
    remainingTime: number | null;
    login: (username: string, password: string) => Promise<LoginResult>;
    logout: () => Promise<boolean>;
    hasRole: (...roles: UserRole[]) => boolean;
    checkAuthStatus: (force?: boolean) => Promise<boolean>;
}

// ─── Context ────────────────────────────────────────────────────────
const AuthContext = createContext<AuthState | null>(null);

/**
 * useAuth – 從 AuthProvider context 取得認證狀態。
 * 必須包在 <AuthProvider> 內使用。
 */
export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx;
}

// ─── Provider ───────────────────────────────────────────────────────
interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const value = useAuthInternal();
    return <AuthContext.Provider value={ value }> { children } </AuthContext.Provider>;
}

// ─── Internal Hook ──────────────────────────────────────────────────
function useAuthInternal(): AuthState {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [remainingTime, setRemainingTime] = useState<number | null>(null);
    // FIX-06: 改用 ref 避免 state 更新非同步造成的 Race Condition
    // errorCount / lastCheck 不需觸發 re-render，用 ref 即可
    const errorCountRef = useRef(0);
    const lastCheckRef = useRef(0);
    // FIX-06: 防止 checkAuthStatus 並發重入
    const isCheckingRef = useRef(false);
    const timeoutWarningShown = useRef(false);
    const inactivityTimerRef = useRef<number | null>(null);
    const { toast } = useToast();

    const isAuthenticated = !!user;
    const role = user?.role ?? null;
    const isAdmin = role === "admin";

    // ── hasRole helper ────────────────────────────────────────────────
    const hasRole = useCallback(
        (...roles: UserRole[]) => !!role && roles.includes(role),
        [role],
    );

    // ── Logout (inner, no toast) ──────────────────────────────────────
    const performLogout = useCallback(async (): Promise<boolean> => {
        try {
            // Get CSRF token
            let csrfToken = "";
            try {
                const csrfRes = await fetch("/api/csrf-token", {
                    method: "GET",
                    credentials: "include",
                });
                if (csrfRes.ok) {
                    const csrfData = await csrfRes.json();
                    csrfToken = csrfData.csrfToken;
                }
            } catch {
                /* ignore */
            }

            const res = await fetch("/api/auth/logout", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Cache-Control": "no-cache, no-store, must-revalidate",
                    ...(csrfToken && { "X-CSRF-Token": csrfToken }),
                },
                credentials: "include",
                cache: "no-store",
            });

            setUser(null);
            setRemainingTime(null);
            setIsReady(true);
            setSentryUser(null);

            // Dispatch status event
            window.dispatchEvent(
                new CustomEvent("adminStatusChanged", { detail: { isAdmin: false } }),
            );

            return res.ok;
        } catch {
            setUser(null);
            setRemainingTime(null);
            setIsReady(true);
            setSentryUser(null);
            return false;
        }
    }, []);

    // ── Inactivity timer ──────────────────────────────────────────────
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current !== null) {
            window.clearTimeout(inactivityTimerRef.current);
        }
        if (user) {
            // 依角色取得閒置逾時（與後端 sessionPolicy 同一份常數）：
            // admin/staff 10 分鐘、driver 12 小時（司機長效登入設計）
            const timeoutMs = getSessionTimeoutMs(user.role);
            inactivityTimerRef.current = window.setTimeout(async () => {
                await performLogout();
                toast({
                    title: "自動登出",
                    description: "由於無操作，系統已自動將您登出",
                    variant: "default",
                });
            }, timeoutMs);
        }
    }, [user, performLogout, toast]);

    // ── Activity listeners ────────────────────────────────────────────
    useEffect(() => {
        if (!user) return;
        const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];
        const handler = () => {
            resetInactivityTimer();
            timeoutWarningShown.current = false;
        };
        events.forEach((ev) => document.addEventListener(ev, handler));
        resetInactivityTimer();
        return () => {
            events.forEach((ev) => document.removeEventListener(ev, handler));
            if (inactivityTimerRef.current !== null) window.clearTimeout(inactivityTimerRef.current);
        };
    }, [user, resetInactivityTimer]);

    // ── Check auth status ─────────────────────────────────────────────
    const checkAuthStatus = useCallback(
        async (force = false): Promise<boolean> => {
            // FIX-06: 防止重入 — 若有請求正在進行中，直接返回當前狀態
            if (isCheckingRef.current) return !!user;

            const now = Date.now();
            const interval = force ? 2000 : 30000;
            // FIX-06: 用 ref 同步更新，避免兩次呼叫同時通過 debounce 檢查
            if (isReady && now - lastCheckRef.current < interval) return !!user;
            lastCheckRef.current = now;
            isCheckingRef.current = true;

            try {
                const res = await fetch("/api/auth/status", {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                    },
                    cache: "no-store",
                });

                if (!res.ok) {
                    // 無條件清除：timer 可能持有掛載時的舊 closure（user=null），
                    // 若用 if (user) 判斷會永遠 skip，session 過期後清不掉前端登入態
                    setUser(null);
                    setRemainingTime(null);
                    setSentryUser(null);
                    return false;
                }

                const data = await res.json();
                if (data.authenticated && data.user) {
                    const authUser: AuthUser = {
                        id: data.user.id,
                        username: data.user.username,
                        displayName: data.user.displayName,
                        role: data.user.role,
                    };
                    setUser(authUser);
                    setSentryUser({
                        id: String(authUser.id),
                        username: authUser.username,
                        role: authUser.role,
                    });

                    // remaining time
                    if (data.remainingTime !== undefined) {
                        setRemainingTime(data.remainingTime);
                        if (data.remainingTime < 60000 && !timeoutWarningShown.current) {
                            timeoutWarningShown.current = true;
                            toast({
                                title: "會話即將過期",
                                description: "您的會話將在不到1分鐘後過期，請繼續操作以保持活動",
                                variant: "destructive",
                            });
                        }
                    }

                    errorCountRef.current = 0; // FIX-06: 成功時重置錯誤計數
                    return true;
                } else {
                    // 同上：無條件清除，不依賴閉包中的 user
                    setUser(null);
                    setRemainingTime(null);
                    setSentryUser(null);
                    return false;
                }
            } catch {
                // FIX-06: 直接操作 ref，不觸發不必要的 re-render
                const next = errorCountRef.current + 1;
                errorCountRef.current = next;
                if (next > 3) {
                    setUser(null);
                    setRemainingTime(null);
                }
                return !!user;
            } finally {
                setIsReady(true);
                isCheckingRef.current = false;
            }
        },
        // FIX-06: 移除 errorCount / lastCheck — 已改為 ref，不再需要在 deps 中追蹤
        [isReady, toast, user],
    );

    // ── Latest-value refs（修正掛載 effect 的 stale closure）──────────────
    // 掛載 effect（deps []）內的 timer 只會捕捉「掛載當下」的 checkAuthStatus 與
    // remainingTime；改經 ref 讀取最新值，否則輪詢間隔恆為預設 5 分鐘、
    // 且過期清除邏輯拿到的永遠是 user=null 的舊 closure
    const checkAuthStatusRef = useRef(checkAuthStatus);
    useEffect(() => {
        checkAuthStatusRef.current = checkAuthStatus;
    }, [checkAuthStatus]);

    const remainingTimeRef = useRef<number | null>(null);
    useEffect(() => {
        remainingTimeRef.current = remainingTime;
    }, [remainingTime]);

    // ── Initialize on mount ───────────────────────────────────────────
    useEffect(() => {
        let timeoutId: number | undefined;

        const scheduleNextCheck = () => {
            // Use remainingTime to intelligently schedule the next status check.
            // Near expiry (< 2 min): check every 30s. Otherwise: check at ~half the remaining time (max 5 min).
            const rt = remainingTimeRef.current;
            let delay: number;
            if (rt !== null && rt > 0) {
                delay = rt < 2 * 60 * 1000 ? 30_000 : Math.min(rt / 2, 5 * 60 * 1000);
            } else {
                delay = 5 * 60 * 1000; // default 5 min for unauthenticated
            }
            timeoutId = window.setTimeout(async () => {
                await checkAuthStatusRef.current(true);
                scheduleNextCheck();
            }, delay);
        };

        checkAuthStatusRef.current(true).then(scheduleNextCheck);

        const handleSessionExpired = () => {
            setUser(null);
            setIsReady(true);
            toast({
                title: "會話已過期",
                description: "請重新登入以繼續操作",
                variant: "destructive",
            });
        };
        window.addEventListener("sessionExpired", handleSessionExpired);

        // 分頁回前景時先向伺服器確認 session 狀態再更新畫面——
        // 背景分頁的 timer 會被瀏覽器節流，回前景時以伺服器狀態為準
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void checkAuthStatusRef.current(true);
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
            window.removeEventListener("sessionExpired", handleSessionExpired);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Login ─────────────────────────────────────────────────────────
    const login = useCallback(
        async (username: string, password: string): Promise<LoginResult> => {
            try {
                const res = await fetch("/api/auth/login", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                    },
                    body: JSON.stringify({ username, password }),
                    credentials: "include",
                    cache: "no-store",
                });

                if (!res.ok) {
                    setUser(null);
                    setIsReady(true);
                    return {
                        ok: false,
                        reason: res.status === 401
                            ? "invalid_credentials"
                            : res.status === 429
                                ? "rate_limited"
                                : "service_unavailable",
                    };
                }

                const data = await res.json();
                if (!data.success) {
                    setUser(null);
                    setIsReady(true);
                    return { ok: false, reason: "service_unavailable" };
                }

                const expectedUserId = data.user?.id;
                if (!Number.isSafeInteger(expectedUserId)) {
                    setUser(null);
                    setRemainingTime(null);
                    setSentryUser(null);
                    setIsReady(true);
                    return { ok: false, reason: "session_unavailable" };
                }

                // FIX-09: 指數退避 100/200/400/800/1600ms（共 ≈ 3.1 秒），改善慢網路下的登入體驗
                const RETRY_DELAYS = [100, 200, 400, 800, 1600];
                let statusData: any = null;
                for (const delay of RETRY_DELAYS) {
                    await new Promise((r) => setTimeout(r, delay));
                    const statusRes = await fetch("/api/auth/status", {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            "Cache-Control": "no-cache, no-store, must-revalidate",
                        },
                        credentials: "include",
                        cache: "no-store",
                    });
                    if (!statusRes.ok) continue;
                    statusData = await statusRes.json();
                    // A previous account can still be authenticated if the new
                    // session cookie has not taken effect. Only accept this login's user.
                    if (statusData.authenticated && statusData.user?.id === expectedUserId) break;
                }

                if (!statusData) {
                    setUser(null);
                    setIsReady(true);
                    return { ok: false, reason: "service_unavailable" };
                }
                if (!statusData.authenticated || statusData.user?.id !== expectedUserId) {
                    setUser(null);
                    setRemainingTime(null);
                    setSentryUser(null);
                    setIsReady(true);
                    return { ok: false, reason: "session_unavailable" };
                }
                if (statusData.authenticated && statusData.user) {
                    const authUser: AuthUser = {
                        id: statusData.user.id,
                        username: statusData.user.username,
                        displayName: statusData.user.displayName,
                        role: statusData.user.role,
                    };
                    setUser(authUser);
                    setSentryUser({
                        id: String(authUser.id),
                        username: authUser.username,
                        role: authUser.role,
                    });

                    // Dispatch backward-compat events
                    window.dispatchEvent(new CustomEvent("adminLoginSuccess"));
                    window.dispatchEvent(
                        new CustomEvent("adminStatusChanged", {
                            detail: { isAdmin: authUser.role === "admin" },
                        }),
                    );
                    setIsReady(true);
                    return { ok: true };
                }

                setUser(null);
                setIsReady(true);
                return { ok: false, reason: "session_unavailable" };
            } catch {
                setUser(null);
                setIsReady(true);
                return { ok: false, reason: "service_unavailable" };
            }
        },
        [],
    );

    // ── Logout (public) ───────────────────────────────────────────────
    const logout = useCallback(async (): Promise<boolean> => {
        return performLogout();
    }, [performLogout]);

    return {
        user,
        isAuthenticated,
        role,
        isAdmin,
        isReady,
        remainingTime,
        login,
        logout,
        hasRole,
        checkAuthStatus,
    };
}
